// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : charts
// =============================================================================

// ---Third-party imports
import React, {Component} from 'react'
import {Grid, Segment} from 'semantic-ui-react'

// ---Framework imports
import log from 'uix/logging'
import {each, filter, findIndex, get, isEmpty, map, orderBy} from 'ldash'
import {useState, useChunk} from 'uix/hooks'

// --- Local imports
import styles from 'uix/styles'
import chartStyle from './charts.scss'
import clsx from 'clsx'
import {default as AutoSizer} from './autosizer/AutoSizer'

// XXX: transform this to functional comp
class PieChartComp extends Component {
  static getLimitedElems = (data, limit, restModifier) => {
    let pieData = []

    if (!isEmpty(data)) {
      let ordered = orderBy(data, e => e.value)
      ordered = ordered.reverse()

      let realElems = []
      let restValue = 0
      if (ordered.length > limit) {
        realElems = ordered.slice(0, limit)
        each(ordered.slice(limit, ordered.length), e => {
          restValue += e.value
        })
      } else {
        realElems = ordered
      }

      pieData = map(realElems, e => ({...e}))

      if (ordered.length > limit) {
        pieData.push({
          name: 'Rest',
          value: restModifier ? restModifier(restValue) : restValue,
          color: '#bbb',
        })
      }
    }

    return pieData
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let realData = []
    if (nextProps.limit) {
      realData = PieChartComp.getLimitedElems(
        nextProps.data,
        nextProps.limit,
        nextProps.restModifier
      )
    } else {
      realData = nextProps.data
    }

    // to be sure the chart will render even if no item has values, set a fake value
    // and ignore it later on
    const filteredByValue = filter(realData, d => d.value)
    if (isEmpty(filteredByValue)) {
      realData = map(realData, d => ({
        ...d,
        value: 1,
        fakeValue: true,
      }))
    }

    let overall = 0
    realData.forEach(d => {
      overall += d.value
    })

    return {
      data: realData,
      overall,
    }
  }

  constructor(props) {
    super(props)

    this.state = {
      activeIndex: null,
      data: [],
      overall: 0,
    }
  }

  onPieEnter = (data, index) => this.setState({activeIndex: index})

  renderActiveShape = ({cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill}) => {
    const {Sector} = this.props.Recharts
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 2}
          outerRadius={outerRadius + 4}
          fill={fill}
        />
      </g>
    )
  }

  renderLegend = () => {
    const {legendValueModifier} = this.props
    const {activeIndex, data, overall} = this.state

    return (
      <>
        {data.map((item, index) => {
          const active = index === activeIndex || activeIndex === null

          return (
            <div
              key={item.name}
              style={{
                paddingTop: '1rem',
                opacity: active ? 1 : 0.3,
                fontSize: '14px',
              }}
              className={chartStyle}
              onMouseEnter={() => this.setState({activeIndex: index})}
              onMouseLeave={() => this.setState({activeIndex: null})}
            >
              <div style={{display: 'flex', flexDirection: 'row'}}>
                <div style={{marginRight: '.5rem'}}>
                  {item.fakeValue
                    ? 0
                    : (legendValueModifier && legendValueModifier(item.value)) || item.value}
                </div>
                <div
                  className={styles.cut}
                  style={{flex: 1, textAlign: 'right', maxWidth: '1000px'}}
                >
                  <span title={item.name}>{item.name}</span>
                </div>
              </div>
              <div style={{borderRadius: '3px', background: 'rgba(0, 0, 0, 0.05)'}}>
                <div
                  style={{
                    width: `${((item.value / overall) * 100).toFixed(2)}%`,
                    height: '6px',
                    marginTop: '.3rem',
                    borderRadius: '3px',
                    background: item.color || 'black',
                  }}
                />
              </div>
            </div>
          )
        })}
      </>
    )
  }

  render() {
    const {noData, aspect = 16.0 / 13.0} = this.props
    const {data, activeIndex, overall} = this.state
    const activeData = get(data, `${activeIndex}`, null)
    const {ResponsiveContainer, PieChart, Pie, Label, Cell} = this.props.Recharts

    if ((!data || isEmpty(data)) && noData) {
      return noData
    }

    // isAnimationActive=false because custom label is not always rendered
    // => https://github.com/recharts/recharts/issues/929
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={7}>{this.renderLegend()}</Grid.Column>
          <Grid.Column width={9}>
            <ResponsiveContainer width="100%" aspect={aspect}>
              <PieChart>
                <Pie
                  activeIndex={activeIndex}
                  activeShape={this.renderActiveShape}
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  outerRadius="90%"
                  innerRadius="80%"
                  onMouseEnter={this.onPieEnter}
                  onMouseLeave={() => this.setState({activeIndex: null})}
                  isAnimationActive={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                  {activeData && (
                    <Label
                      value={`${((activeData.value / overall) * 100).toFixed(2)}%`}
                      position="center"
                      style={{
                        fontSize: '2vw',
                        fontWeight: 'bold',
                        fill: activeData.color,
                      }}
                    />
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }
}

const PieChart = props => {
  let Recharts = useChunk('Recharts')
  if (!Recharts) return null
  props = {...props, Recharts}
  return <PieChartComp {...props} />
}

const BarChart = ({data, chartType, aspect = 16.0 / 10.0, tooltipValueModifier}) => {
  let [showLegend, setShowLegend] = useState(false)
  let [hoverIndex, setHoverIndex] = useState(null)

  let Recharts = useChunk('Recharts')
  if (!Recharts) return null

  let {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    YAxis,
    XAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    Cell,
  } = Recharts
  // <button onClick={() => setShowLegend(!showLegend)}>
  //   toggle legend
  // </button>
  let xAxis = (
    <XAxis
      dataKey="x"
      label={{value: '', position: 'insideBottomRight', offset: 0}}
      dy={15}
      fontSize={10}
      angle={-45}
    />
  )

  let yAxis = <YAxis yAxisId="yAxis" fontSize={12} stroke="var(--vl_accent_color)" />

  let toolTip = (
    <Tooltip content={<BarChartTooltip valueModifier={tooltipValueModifier} />} cursor={false} />
  )

  let legend = (
    <Legend
      wrapperStyle={{
        visibility: showLegend ? 'visible' : 'hidden',
        top: '35px',
        left: 0,
        width: '200px',
        background: 'var(--vl_accent_color)',
        borderRadius: '3px',
        border: '1px solid #789',
      }}
    />
  )

  let grid = <CartesianGrid stroke="var(--vl_borderColor)" />

  let chart = null
  if (chartType === 'line') {
    chart = (
      <LineChart width={100} height={32} data={data}>
        {xAxis}
        {yAxis}
        {toolTip}
        {legend}
        {grid}
        <Line
          type="monotone"
          dataKey="y"
          yAxisId="yAxis"
          stroke="var(--vl_accent_color)"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    )
  } else if (chartType === 'bar') {
    chart = (
      <BarChart data={data} margin={{top: 20, right: 40, bottom: 30, left: 20}}>
        {xAxis}
        {yAxis}
        {toolTip}
        {legend}
        {grid}
        <Bar
          yAxisId="yAxis"
          dataKey="y"
          barSize={20}
          onMouseEnter={(e, i) => setHoverIndex(i)}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {data.map((entry, index) => (
            <Cell
              fill={
                index === hoverIndex ? 'var(--vl_accent_color-hover)' : 'var(--vl_accent_color)'
              }
              key={`cell-${entry.date}`}
            />
          ))}
        </Bar>
      </BarChart>
    )
  }

  return (
    <ResponsiveContainer width="100%" aspect={aspect}>
      {chart}
    </ResponsiveContainer>
  )
}

const BarChartTooltip = ({active, payload, label, valueModifier}) => {
  if (active) {
    const value = get(payload, ['0', 'value'], '')
    return <Segment raised>{`${label}: ${valueModifier ? valueModifier(value) : value}`}</Segment>
  }

  return null
}

const AreaChartTooltip = ({active, payload, label, tooltip}) => {
  if (active) {
    const values = [get(payload, ['0', 'value'], ''), get(payload, ['1', 'value'], '')]
    const {valueModifiers, labels} = tooltip

    let a = 0
    let b = 1
    if (values[a] < values[b]) {
      a = 1
      b = 0
    }

    return (
      <Segment raised>
        <div style={{fontWeight: 'bold'}}>{label}</div>
        <div
          className={clsx(
            chartStyle.areaChartTooltipLabel,
            a == 0 ? chartStyle.left : chartStyle.right
          )}
        >
          {`${valueModifiers[a] ? valueModifiers[a](values[a]) : values[a]} ${
            labels[a] ? labels[a] : ''
          }`}
        </div>
        <div
          className={clsx(
            chartStyle.areaChartTooltipLabel,
            a !== 0 ? chartStyle.left : chartStyle.right
          )}
        >{`${valueModifiers[b] ? valueModifiers[b](values[b]) : values[b]} ${
          labels[b] ? labels[b] : ''
        }`}</div>
      </Segment>
    )
  }

  return null
}

const AreaChart = ({data, coupleYAxes, chartType, aspect = 21.0 / 9.0, tooltip, labels}) => {
  const [hoverIndex, setHoverIndex] = useState(null)
  const [barChartHoverBackground, setBarChartHoverBackground] = useState(null)

  let yAxisDataLeft = 'y1'
  let yAxisDataRight = 'y2'

  const rightAxisHasData = findIndex(data, e => e.y2 !== null) > -1
  if (coupleYAxes && rightAxisHasData) {
    let y1 = data.map(i => {
      return i.y1
    })
    let y2 = data.map(i => {
      return i.y2
    })

    if (Math.max(...y2) > Math.max(...y1)) {
      yAxisDataLeft = 'y2'
      yAxisDataRight = 'y2'
    } else {
      yAxisDataLeft = 'y1'
      yAxisDataRight = 'y1'
    }
  }

  let Recharts = useChunk('Recharts')
  if (!Recharts) return null

  const {
    ResponsiveContainer,
    YAxis,
    XAxis,
    Tooltip,
    CartesianGrid,
    Label,
    LineChart,
    Line,
    BarChart,
    Bar,
    Cell,
  } = Recharts

  const xAxis = (
    <XAxis
      dataKey="x"
      label={{value: '', position: 'insideBottomRight', offset: 0}}
      dy={15}
      fontSize={11}
      angle={-45}
    />
  )

  const yAxisLeft = (
    <YAxis
      yAxisId="val1"
      dataKey={yAxisDataLeft}
      fontSize={'var(--vl_secondaryBody_fontSize)'}
      stroke="var(--vl_accent_color)"
      tickFormatter={labels.valueModifiers[0]}
    >
      <Label
        className={`${chartStyle.areaChartLabel} ${chartStyle.left}`}
        position="insideLeft"
        value={labels.values[0]}
        angle={-90}
      />
    </YAxis>
  )

  const yAxisRight = (
    <YAxis
      yAxisId={'val2'}
      dataKey={yAxisDataRight}
      orientation="right"
      fontSize={'var(--vl_secondaryBody_fontSize)'}
      stroke="var(--vl_accent_color2)"
      tickFormatter={labels.valueModifiers[1]}
    >
      <Label
        className={`${chartStyle.areaChartLabel} ${chartStyle.right}`}
        position="insideRight"
        value={labels.values[1]}
        angle={90}
      />
    </YAxis>
  )

  const grid = <CartesianGrid stroke="var(--vl_borderColor)" strokeDasharray="3 3" />

  const toolTip = (
    <Tooltip
      content={<AreaChartTooltip tooltip={tooltip} />}
      cursor={{fill: 'var(--vl_content_bgColor)'}}
    />
  )

  let chart = null
  if (chartType === 'line') {
    chart = (
      <LineChart data={data} margin={{top: 20, right: 40, bottom: 30, left: 20}}>
        {xAxis}
        {yAxisLeft}
        {yAxisRight}
        {grid}
        {toolTip}
        <Line
          type="monotone"
          dataKey="y1"
          stroke="var(--vl_accent_color)"
          yAxisId="val1"
          dot={false}
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="y2"
          stroke="var(--vl_accent_color2)"
          yAxisId="val2"
          dot={false}
          strokeWidth={2}
        />
      </LineChart>
    )
  } else if (chartType === 'bar') {
    chart = (
      <BarChart data={data} margin={{top: 20, right: 40, bottom: 30, left: 20}}>
        {xAxis}
        {yAxisLeft}
        {yAxisRight}
        {toolTip}
        {grid}
        <Bar yAxisId="val1" dataKey="y1" barSize={20}>
          {data.map((entry, index) => (
            <Cell fill={'var(--vl_accent_color)'} key={`cell-${entry.date}`} />
          ))}
        </Bar>
        <Bar
          yAxisId="val2"
          dataKey="y2"
          barSize={20}
          onMouseEnter={(e, i) => setHoverIndex(i)}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {data.map((entry, index) => (
            <Cell fill={'var(--vl_accent_color2)'} key={`cell-${entry.date}`} />
          ))}
        </Bar>
      </BarChart>
    )
  }

  return (
    <ResponsiveContainer width="100%" aspect={aspect}>
      {chart}
    </ResponsiveContainer>
  )
}

export {PieChart, BarChart, AreaChart}

const TooltipCursor = props => {
  let Recharts = useChunk('Recharts')
  if (!Recharts) return null

  const {x, y, width, height, fill, payloadIndex, onClick, payload} = props
  return (
    <Recharts.Rectangle
      x={x}
      y={y}
      fill={'#EEE7'}
      width={width}
      height={height}
      onClick={() => onClick(payload[0])}
    />
  )
}
export const Histogram = ({data, dataKey = 'count', range100 = false, onBarClick}) => {
  // renders data = [{x, count: 10}, ...] as barchart histogram
  let Recharts = useChunk('Recharts')
  if (!Recharts) return null
  let {BarChart, Bar, Tooltip, XAxis, Rectangle} = Recharts
  let fmt_x = v => `${v} %`
  let domain = [0, 100]
  if (!range100 && data.length) {
    let xs = data.map(e => e.x)
    domain = [Math.min(...xs), Math.max(...xs)]
  }

  return (
    <AutoSizer>
      <BarChart width={150} height={40} data={data}>
        <Bar dataKey={dataKey} fill={'var(--vl_accent_color)'} />
        {data.length ? (
          <XAxis
            dataKey="x"
            type={'number'}
            domain={domain}
            fontSize={11}
            tickFormatter={fmt_x}
            interval={0}
            padding={{left: 5, right: 10}}
          />
        ) : null}
        <Tooltip
          labelFormatter={fmt_x}
          cursor={<TooltipCursor onClick={({payload}) => onBarClick(payload.x)} />}
        />
      </BarChart>
    </AutoSizer>
  )
}
